import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import TaxOfficeSelect from "./TaxOfficeSelect";
import { isGermanyCountry } from "../utils/taxOfficeUtils";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";

const POLAND_COUNTRY_ID = 17;

const emptyEntityErrors = {
  companyName: "",
  companyAddress: "",
  companyPostalCode: "",
  companyCity: "",
  companyProvince: "",
  countryId: "",
  coc: "",
  vat: "",
  steuerNummer: "",
  taxOfficeId: "",
  website: "",
  email: "",
  phonenumber: "",
  startupDate: "",
};

const formatDateForInput = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const isPoland = (countryId) => Number(countryId) === POLAND_COUNTRY_ID;

const createFormValuesFromEntity = (entity) => ({
  entityId: entity.entityId || entity.id,
  entityNumber: entity.entityNumber || "",
  companyName: entity.name || "",
  companyAddress: entity.address || "",
  houseNumber: entity.houseNumber || "",
  companyPostalCode: entity.postalCode || "",
  companyCity: entity.city || "",
  companyProvince: entity.province || "",
  companyCounty: entity.county || "",
  companyMunicipality: entity.municipality || "",
  postOffice: entity.postOffice || "",
  countryId: entity.countryId || "",
  coc: entity.coC || "",
  vat: entity.vat || "",
  steuerNummer: entity.steuerNummer || "",
  taxOfficeId: entity.taxOfficeId || "",
  website: entity.website || "",
  email: entity.generalEmailAddress || "",
  phonenumber: entity.generalPhoneNumber || "",
  startupDate: formatDateForInput(entity.startupDate),
});

const validateEntityForm = (values, countries = []) => {
  const errors = { ...emptyEntityErrors };

  if (!values.companyName?.trim()) {
    errors.companyName = "Required";
  }
  if (!values.companyAddress?.trim()) {
    errors.companyAddress = "Required";
  }
  if (!values.companyPostalCode?.trim()) {
    errors.companyPostalCode = "Required";
  }
  if (!values.companyCity?.trim()) {
    errors.companyCity = "Required";
  }
  if (!values.companyProvince?.trim()) {
    errors.companyProvince = "Required";
  }
  if (!values.countryId) {
    errors.countryId = "Required";
  }
  if (!values.coc?.trim()) {
    errors.coc = "Required";
  }
  if (!values.vat?.trim()) {
    errors.vat = "Required";
  }
  if (isGermanyCountry(values.countryId, countries) && !values.steuerNummer?.trim()) {
    errors.steuerNummer = "Required";
  }
  if (!values.taxOfficeId) {
    errors.taxOfficeId = "Required";
  }
  if (!values.website?.trim()) {
    errors.website = "Required";
  }
  if (!values.email?.trim()) {
    errors.email = "Required";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
    errors.email = "Please enter a valid email address";
  }
  if (!values.phonenumber?.trim()) {
    errors.phonenumber = "Required";
  }
  if (!values.startupDate) {
    errors.startupDate = "Required";
  }

  return errors;
};

const countEntityErrors = (errors = emptyEntityErrors) =>
  Object.values(errors).filter(Boolean).length;

const getMissingFieldsLabel = (count) =>
  count === 1 ? "1 required field is missing" : `${count} required fields are missing`;

const allRequiredFieldsMetLabel = "All required fields are filled";

const getEntityPayload = (entity, formValues, countries = []) => {
  const entityIsPoland = isPoland(formValues.countryId);
  const entityIsGermany = isGermanyCountry(formValues.countryId, countries);

  return {
    entityNumber: formValues.entityNumber.trim(),
    typeRelation: entity.typeRelation,
    name: formValues.companyName.trim(),
    address: formValues.companyAddress.trim(),
    postalCode: formValues.companyPostalCode.trim(),
    city: formValues.companyCity.trim(),
    province: formValues.companyProvince.trim(),
    countryId: formValues.countryId,
    coC: formValues.coc.trim(),
    vat: formValues.vat.trim(),
    steuerNummer: entityIsGermany ? (formValues.steuerNummer || "").trim() : "",
    taxOfficeId: formValues.taxOfficeId,
    website: formValues.website.trim(),
    generalEmailAddress: formValues.email.trim(),
    generalPhoneNumber: formValues.phonenumber.trim(),
    startupDate: formValues.startupDate,
    modifiedDate: new Date().toISOString(),
    isDisabled: entity.isDisabled ?? true,
    entityStatusId: entity.entityStatusId ?? 0,
    supplierId: entity.supplierId ?? 0,
    houseNumber: entityIsPoland ? formValues.houseNumber.trim() : "",
    county: entityIsPoland ? formValues.companyCounty.trim() : "",
    municipality: entityIsPoland ? formValues.companyMunicipality.trim() : "",
    postOffice: entityIsPoland ? formValues.postOffice.trim() : "",
  };
};

const getErrorMessage = (error, fallbackMessage) =>
  error?.response?.data?.detail || error?.response?.data?.message || error?.message || fallbackMessage;

const ConvertProspectEntitiesDialog = ({ open, onClose, onConverted, prospectId, prospectName }) => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [entities, setEntities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedEntityIds, setSelectedEntityIds] = useState([]);
  const [entityForms, setEntityForms] = useState({});
  const [entityErrors, setEntityErrors] = useState({});
  const [activeEntityId, setActiveEntityId] = useState(null);
  const [converting, setConverting] = useState(false);
  const [submissionError, setSubmissionError] = useState("");
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open || !prospectId) {
      setLoading(false);
      setStep(0);
      setEntities([]);
      setCountries([]);
      setSelectedEntityIds([]);
      setEntityForms({});
      setEntityErrors({});
      setActiveEntityId(null);
      setConverting(false);
      setSubmissionError("");
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setSubmissionError("");

      try {
        const [countriesResponse, prospectResponse] = await Promise.all([
          axios.get("/api/v1/countries"),
          axios.get(`/api/v1/prospects/${prospectId}`),
        ]);

        setCountries(countriesResponse.data || []);

        const prospectEntityIds = prospectResponse.data?.entityIds || [];
        if (prospectEntityIds.length === 0) {
          setEntities([]);
          return;
        }

        const entityResponses = await Promise.all(
          prospectEntityIds.map((entityId) =>
            axios.get(`/api/v1/entities/${entityId}`).catch(() => null)
          )
        );

        const fetchedEntities = entityResponses
          .filter(Boolean)
          .map((response) => {
            const entity = response.data;
            return {
              ...entity,
              entityId: entity.entityId || entity.id,
            };
          })
          .filter((entity) => entity.entityId);

        setEntities(fetchedEntities);
      } catch (error) {
        setSubmissionError(getErrorMessage(error, "Failed to load prospect entities."));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [open, prospectId]);

  const selectedEntities = useMemo(
    () => entities.filter((entity) => selectedEntityIds.includes(entity.entityId)),
    [entities, selectedEntityIds]
  );

  const activeEntityForm = activeEntityId ? entityForms[activeEntityId] : null;
  const activeEntityFormErrors = activeEntityId ? entityErrors[activeEntityId] || emptyEntityErrors : emptyEntityErrors;
  const handleClose = () => {
    if (converting) {
      return;
    }

    onClose();
  };

  const handleToggleEntitySelection = (entityId, checked) => {
    setSelectedEntityIds((previousIds) => {
      if (checked) {
        return [...previousIds, entityId];
      }

      return previousIds.filter((id) => id !== entityId);
    });
  };

  const handleContinueToDetails = () => {
    const nextEntityForms = {};
    const nextEntityErrors = {};

    selectedEntities.forEach((entity) => {
      const entityId = entity.entityId;
      const formValues = entityForms[entityId] || createFormValuesFromEntity(entity);
      nextEntityForms[entityId] = formValues;
      nextEntityErrors[entityId] = validateEntityForm(formValues, countries);
    });

    const firstInvalidEntity = selectedEntities.find(
      (entity) => countEntityErrors(nextEntityErrors[entity.entityId]) > 0
    );

    setEntityForms((previousForms) => ({
      ...previousForms,
      ...nextEntityForms,
    }));
    setEntityErrors(nextEntityErrors);
    setActiveEntityId(firstInvalidEntity?.entityId || selectedEntities[0]?.entityId || null);
    setSubmissionError("");
    setStep(1);
  };

  const handleFormChange = (entityId, field, value) => {
    setEntityForms((previousForms) => {
      const currentValues = previousForms[entityId] || createFormValuesFromEntity(
        entities.find((entity) => entity.entityId === entityId) || {}
      );
      const nextValues = {
        ...currentValues,
        [field]: value,
      };

      if (field === "countryId") {
        nextValues.taxOfficeId = "";
        if (!isPoland(value)) {
          nextValues.houseNumber = "";
          nextValues.companyCounty = "";
          nextValues.companyMunicipality = "";
          nextValues.postOffice = "";
        }
        if (!isGermanyCountry(value, countries)) {
          nextValues.steuerNummer = "";
        }
      }

      setEntityErrors((previousErrors) => ({
        ...previousErrors,
        [entityId]: validateEntityForm(nextValues, countries),
      }));

      return {
        ...previousForms,
        [entityId]: nextValues,
      };
    });

    setSubmissionError("");
  };

  const handleConvert = async () => {
    const nextEntityErrors = {};

    selectedEntities.forEach((entity) => {
      const formValues = entityForms[entity.entityId] || createFormValuesFromEntity(entity);
      nextEntityErrors[entity.entityId] = validateEntityForm(formValues, countries);
    });

    const firstInvalidEntity = selectedEntities.find(
      (entity) => countEntityErrors(nextEntityErrors[entity.entityId]) > 0
    );

    setEntityErrors(nextEntityErrors);

    if (firstInvalidEntity) {
      setActiveEntityId(firstInvalidEntity.entityId);
      setSubmissionError("Complete all required customer fields before converting.");
      return;
    }

    setConverting(true);
    setSubmissionError("");

    try {
      for (const entity of selectedEntities) {
        const formValues = entityForms[entity.entityId];
        const payload = getEntityPayload(entity, formValues, countries);

        await axios.put(`/api/v1/entities/${entity.entityId}`, payload);
      }

      const response = await axios.post(`/api/v1/prospects/${prospectId}/convert-to-customers`, {
        entityIds: selectedEntityIds,
      });

      setResult({
        success: true,
        message: response.data?.message || "Prospect entities have been converted to customers successfully.",
        createdCustomers: response.data?.createdCustomers || [],
      });
      setResultDialogOpen(true);
      onClose();
      if (onConverted) {
        onConverted();
      }
    } catch (error) {
      setSubmissionError(getErrorMessage(error, "Failed to convert the selected entities."));
    } finally {
      setConverting(false);
    }
  };

  const isCurrentEntityPoland = isPoland(activeEntityForm?.countryId);
  const isCurrentEntityGermany = isGermanyCountry(activeEntityForm?.countryId, countries);

  return (
    <React.Fragment>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          {step === 0 ? "Convert Entity(ies) to Customer(s)" : "Complete Customer Information"}
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ pt: 1 }}>
              {submissionError ? (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {submissionError}
                </Alert>
              ) : null}

              {step === 0 ? (
                <React.Fragment>
                  <Typography variant="body1" sx={{ mb: 3 }}>
                    Select which entities from prospect <strong>{prospectName || "-"}</strong> should be converted to customers.
                  </Typography>

                  {entities.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No entities found for this prospect.
                    </Typography>
                  ) : (
                    <Box>
                      <Box
                        sx={{
                          maxHeight: 420,
                          overflowY: "auto",
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          p: 2,
                        }}
                      >
                        {entities.map((entity) => {
                          const entityId = entity.entityId;
                          const formValues = entityForms[entityId] || createFormValuesFromEntity(entity);
                          const missingFieldsCount = countEntityErrors(validateEntityForm(formValues, countries));
                          const isSelected = selectedEntityIds.includes(entityId);

                          return (
                            <Box
                              key={entityId}
                              sx={{
                                p: 1.5,
                                mb: 1,
                                borderRadius: 1,
                                border: "1px solid",
                                borderColor: isSelected ? "primary.main" : "divider",
                                backgroundColor: isSelected ? "action.selected" : "transparent",
                              }}
                            >
                              <FormControlLabel
                                sx={{ width: "100%", m: 0, alignItems: "flex-start" }}
                                control={
                                  <Checkbox
                                    checked={isSelected}
                                    onChange={(event) =>
                                      handleToggleEntitySelection(entityId, event.target.checked)
                                    }
                                  />
                                }
                                label={
                                  <Box sx={{ pt: 0.5 }}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        gap: 1,
                                        alignItems: "flex-start",
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      <Box>
                                        <Typography variant="body1" fontWeight={500}>
                                          {entity.entityNumber || "N/A"} - {entity.name || "Unnamed Entity"}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          Relation Type will be changed to Customer during conversion.
                                        </Typography>
                                      </Box>
                                      <Chip
                                        size="small"
                                        label={
                                          missingFieldsCount > 0
                                            ? getMissingFieldsLabel(missingFieldsCount)
                                            : allRequiredFieldsMetLabel
                                        }
                                        color={missingFieldsCount > 0 ? "warning" : "success"}
                                        variant={missingFieldsCount > 0 ? "filled" : "outlined"}
                                      />
                                    </Box>
                                    {missingFieldsCount > 0 ? (
                                      <Typography variant="body2" color="warning.main" sx={{ mt: 0.75 }}>
                                        Required fields are still missing for this entity.
                                      </Typography>
                                    ) : (
                                      <Typography variant="body2" color="success.main" sx={{ mt: 0.75 }}>
                                        All required customer fields are already present.
                                      </Typography>
                                    )}
                                  </Box>
                                }
                              />
                            </Box>
                          );
                        })}
                      </Box>

                      <Box
                        sx={{
                          mt: 2,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Button
                          size="small"
                          onClick={() => {
                            if (selectedEntityIds.length === entities.length) {
                              setSelectedEntityIds([]);
                              return;
                            }

                            setSelectedEntityIds(entities.map((entity) => entity.entityId));
                          }}
                        >
                          {selectedEntityIds.length === entities.length ? "Deselect All" : "Select All"}
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                          {selectedEntityIds.length} of {entities.length} entity(ies) selected
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    Complete the required customer fields below.
                  </Alert>

                  <Tabs
                    value={activeEntityId || false}
                    onChange={(_, value) => setActiveEntityId(value)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ mb: 3 }}
                  >
                    {selectedEntities.map((entity) => {
                      const entityId = entity.entityId;
                      const formErrors = entityErrors[entityId] || emptyEntityErrors;
                      const errorCount = countEntityErrors(formErrors);

                      return (
                        <Tab
                          key={entityId}
                          value={entityId}
                          label={
                            <Box sx={{ py: 0.5, textAlign: "left" }}>
                              <Typography variant="body2" fontWeight={600}>
                                {entity.entityNumber || "N/A"}
                              </Typography>
                              <Chip
                                size="small"
                                label={errorCount > 0 ? getMissingFieldsLabel(errorCount) : allRequiredFieldsMetLabel}
                                color={errorCount > 0 ? "warning" : "success"}
                                variant={errorCount > 0 ? "filled" : "outlined"}
                                sx={{ mt: 0.75 }}
                              />
                            </Box>
                          }
                        />
                      );
                    })}
                  </Tabs>

                  {activeEntityForm ? (
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="h6">
                            {activeEntityForm.companyName || "Unnamed Entity"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Required customer information for entity {activeEntityForm.entityNumber || "-"}
                          </Typography>
                        </Box>

                        <Grid container spacing={2}>
                          <Grid item md={4} xs={12}>
                            <TextField
                              fullWidth
                              label="Customer Number"
                              value={activeEntityForm.entityNumber}
                              disabled
                              helperText="Generated by the backend during conversion"
                            />
                          </Grid>
                          <Grid item md={6} xs={12}>
                            <TextField
                              fullWidth
                              required
                              label="Company Name"
                              value={activeEntityForm.companyName}
                              onChange={(event) =>
                                handleFormChange(activeEntityId, "companyName", event.target.value)
                              }
                              error={Boolean(activeEntityFormErrors.companyName)}
                              helperText={activeEntityFormErrors.companyName}
                            />
                          </Grid>
                          <Grid item md={2} xs={12}>
                            <TextField fullWidth label="Relation Type" value="Customer" disabled />
                          </Grid>
                        </Grid>

                        {isCurrentEntityPoland ? (
                          <React.Fragment>
                            <Grid container spacing={2} sx={{ mt: 0.5 }}>
                              <Grid item md={4} xs={12}>
                                <TextField
                                  fullWidth
                                  required
                                  label="Street"
                                  value={activeEntityForm.companyAddress}
                                  onChange={(event) =>
                                    handleFormChange(activeEntityId, "companyAddress", event.target.value)
                                  }
                                  error={Boolean(activeEntityFormErrors.companyAddress)}
                                  helperText={activeEntityFormErrors.companyAddress}
                                />
                              </Grid>
                              <Grid item md={2} xs={12}>
                                <TextField
                                  fullWidth
                                  label="House Number"
                                  value={activeEntityForm.houseNumber}
                                  onChange={(event) =>
                                    handleFormChange(activeEntityId, "houseNumber", event.target.value)
                                  }
                                />
                              </Grid>
                              <Grid item md={2} xs={12}>
                                <TextField
                                  fullWidth
                                  required
                                  label="Postal Code"
                                  value={activeEntityForm.companyPostalCode}
                                  onChange={(event) =>
                                    handleFormChange(activeEntityId, "companyPostalCode", event.target.value)
                                  }
                                  error={Boolean(activeEntityFormErrors.companyPostalCode)}
                                  helperText={activeEntityFormErrors.companyPostalCode}
                                />
                              </Grid>
                              <Grid item md={4} xs={12}>
                                <TextField
                                  fullWidth
                                  required
                                  label="City"
                                  value={activeEntityForm.companyCity}
                                  onChange={(event) =>
                                    handleFormChange(activeEntityId, "companyCity", event.target.value)
                                  }
                                  error={Boolean(activeEntityFormErrors.companyCity)}
                                  helperText={activeEntityFormErrors.companyCity}
                                />
                              </Grid>
                            </Grid>

                            <Grid container spacing={2} sx={{ mt: 0.5 }}>
                              <Grid item md={3} xs={12}>
                                <TextField
                                  fullWidth
                                  required
                                  label="Province"
                                  value={activeEntityForm.companyProvince}
                                  onChange={(event) =>
                                    handleFormChange(activeEntityId, "companyProvince", event.target.value)
                                  }
                                  error={Boolean(activeEntityFormErrors.companyProvince)}
                                  helperText={activeEntityFormErrors.companyProvince}
                                />
                              </Grid>
                              <Grid item md={3} xs={12}>
                                <TextField
                                  fullWidth
                                  label="County"
                                  value={activeEntityForm.companyCounty}
                                  onChange={(event) =>
                                    handleFormChange(activeEntityId, "companyCounty", event.target.value)
                                  }
                                />
                              </Grid>
                              <Grid item md={4} xs={12}>
                                <TextField
                                  fullWidth
                                  label="Municipality"
                                  value={activeEntityForm.companyMunicipality}
                                  onChange={(event) =>
                                    handleFormChange(activeEntityId, "companyMunicipality", event.target.value)
                                  }
                                />
                              </Grid>
                              <Grid item md={2} xs={12}>
                                <FormControl fullWidth required error={Boolean(activeEntityFormErrors.countryId)}>
                                  <InputLabel>Country</InputLabel>
                                  <Select
                                    value={activeEntityForm.countryId}
                                    label="Country"
                                    onChange={(event) =>
                                      handleFormChange(activeEntityId, "countryId", event.target.value)
                                    }
                                  >
                                    {countries.map((country) => (
                                      <MenuItem key={country.countryId} value={country.countryId}>
                                        {country.countryCode}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                {activeEntityFormErrors.countryId ? (
                                  <Typography variant="caption" color="error">
                                    {activeEntityFormErrors.countryId}
                                  </Typography>
                                ) : null}
                              </Grid>
                            </Grid>
                          </React.Fragment>
                        ) : (
                          <React.Fragment>
                            <Grid container spacing={2} sx={{ mt: 0.5 }}>
                              <Grid item md={8} xs={12}>
                                <TextField
                                  fullWidth
                                  required
                                  label="Address"
                                  value={activeEntityForm.companyAddress}
                                  onChange={(event) =>
                                    handleFormChange(activeEntityId, "companyAddress", event.target.value)
                                  }
                                  error={Boolean(activeEntityFormErrors.companyAddress)}
                                  helperText={activeEntityFormErrors.companyAddress}
                                />
                              </Grid>
                              <Grid item md={4} xs={12}>
                                <TextField
                                  fullWidth
                                  required
                                  label="Postal Code"
                                  value={activeEntityForm.companyPostalCode}
                                  onChange={(event) =>
                                    handleFormChange(activeEntityId, "companyPostalCode", event.target.value)
                                  }
                                  error={Boolean(activeEntityFormErrors.companyPostalCode)}
                                  helperText={activeEntityFormErrors.companyPostalCode}
                                />
                              </Grid>
                            </Grid>

                            <Grid container spacing={2} sx={{ mt: 0.5 }}>
                              <Grid item md={5} xs={12}>
                                <TextField
                                  fullWidth
                                  required
                                  label="City"
                                  value={activeEntityForm.companyCity}
                                  onChange={(event) =>
                                    handleFormChange(activeEntityId, "companyCity", event.target.value)
                                  }
                                  error={Boolean(activeEntityFormErrors.companyCity)}
                                  helperText={activeEntityFormErrors.companyCity}
                                />
                              </Grid>
                              <Grid item md={5} xs={12}>
                                <TextField
                                  fullWidth
                                  required
                                  label="Province"
                                  value={activeEntityForm.companyProvince}
                                  onChange={(event) =>
                                    handleFormChange(activeEntityId, "companyProvince", event.target.value)
                                  }
                                  error={Boolean(activeEntityFormErrors.companyProvince)}
                                  helperText={activeEntityFormErrors.companyProvince}
                                />
                              </Grid>
                              <Grid item md={2} xs={12}>
                                <FormControl fullWidth required error={Boolean(activeEntityFormErrors.countryId)}>
                                  <InputLabel>Country</InputLabel>
                                  <Select
                                    value={activeEntityForm.countryId}
                                    label="Country"
                                    onChange={(event) =>
                                      handleFormChange(activeEntityId, "countryId", event.target.value)
                                    }
                                  >
                                    {countries.map((country) => (
                                      <MenuItem key={country.countryId} value={country.countryId}>
                                        {country.countryCode}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                {activeEntityFormErrors.countryId ? (
                                  <Typography variant="caption" color="error">
                                    {activeEntityFormErrors.countryId}
                                  </Typography>
                                ) : null}
                              </Grid>
                            </Grid>
                          </React.Fragment>
                        )}

                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                          <Grid item md={6} xs={12}>
                            <TextField
                              fullWidth
                              required
                              label="General Email Address"
                              type="email"
                              value={activeEntityForm.email}
                              onChange={(event) =>
                                handleFormChange(activeEntityId, "email", event.target.value)
                              }
                              error={Boolean(activeEntityFormErrors.email)}
                              helperText={activeEntityFormErrors.email}
                            />
                          </Grid>
                          <Grid item md={6} xs={12}>
                            <TextField
                              fullWidth
                              required
                              label="General Phone Number"
                              value={activeEntityForm.phonenumber}
                              onChange={(event) =>
                                handleFormChange(activeEntityId, "phonenumber", event.target.value)
                              }
                              error={Boolean(activeEntityFormErrors.phonenumber)}
                              helperText={activeEntityFormErrors.phonenumber}
                            />
                          </Grid>
                        </Grid>

                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                          <Grid item md={isCurrentEntityGermany ? 4 : 6} xs={12}>
                            <TextField
                              fullWidth
                              required
                              label="Chamber of Commerce"
                              value={activeEntityForm.coc}
                              onChange={(event) =>
                                handleFormChange(activeEntityId, "coc", event.target.value)
                              }
                              error={Boolean(activeEntityFormErrors.coc)}
                              helperText={activeEntityFormErrors.coc}
                            />
                          </Grid>
                          <Grid item md={isCurrentEntityGermany ? 4 : 6} xs={12}>
                            <TextField
                              fullWidth
                              required
                              label="VAT"
                              value={activeEntityForm.vat}
                              onChange={(event) =>
                                handleFormChange(activeEntityId, "vat", event.target.value)
                              }
                              error={Boolean(activeEntityFormErrors.vat)}
                              helperText={activeEntityFormErrors.vat}
                            />
                          </Grid>
                          {isCurrentEntityGermany ? (
                            <Grid item md={4} xs={12}>
                              <TextField
                                fullWidth
                                required
                                label="Steuernummer"
                                value={activeEntityForm.steuerNummer}
                                onChange={(event) =>
                                  handleFormChange(activeEntityId, "steuerNummer", event.target.value)
                                }
                                error={Boolean(activeEntityFormErrors.steuerNummer)}
                                helperText={activeEntityFormErrors.steuerNummer}
                                inputProps={{ "aria-label": "Steuernummer" }}
                              />
                            </Grid>
                          ) : null}
                        </Grid>

                        {isCurrentEntityPoland ? (
                          <Grid container spacing={2} sx={{ mt: 0.5 }}>
                            <Grid item md={6} xs={12}>
                              <TaxOfficeSelect
                                value={activeEntityForm.taxOfficeId}
                                countryId={activeEntityForm.countryId}
                                error={activeEntityFormErrors.taxOfficeId}
                                helperText={activeEntityFormErrors.taxOfficeId}
                                onChange={(event) =>
                                  handleFormChange(activeEntityId, "taxOfficeId", event.target.value)
                                }
                              />
                            </Grid>
                            <Grid item md={6} xs={12}>
                              <TextField
                                fullWidth
                                label="Post Office"
                                value={activeEntityForm.postOffice}
                                onChange={(event) =>
                                  handleFormChange(activeEntityId, "postOffice", event.target.value)
                                }
                              />
                            </Grid>
                          </Grid>
                        ) : (
                          <Grid container spacing={2} sx={{ mt: 0.5 }}>
                            <Grid item xs={12}>
                              <TaxOfficeSelect
                                value={activeEntityForm.taxOfficeId}
                                countryId={activeEntityForm.countryId}
                                error={activeEntityFormErrors.taxOfficeId}
                                helperText={activeEntityFormErrors.taxOfficeId}
                                onChange={(event) =>
                                  handleFormChange(activeEntityId, "taxOfficeId", event.target.value)
                                }
                              />
                            </Grid>
                          </Grid>
                        )}

                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                          <Grid item md={6} xs={12}>
                            <TextField
                              fullWidth
                              required
                              label="Website"
                              value={activeEntityForm.website}
                              onChange={(event) =>
                                handleFormChange(activeEntityId, "website", event.target.value)
                              }
                              error={Boolean(activeEntityFormErrors.website)}
                              helperText={activeEntityFormErrors.website}
                            />
                          </Grid>
                          <Grid item md={6} xs={12}>
                            <TextField
                              fullWidth
                              required
                              label="Start Date"
                              type="date"
                              value={activeEntityForm.startupDate}
                              onChange={(event) =>
                                handleFormChange(activeEntityId, "startupDate", event.target.value)
                              }
                              error={Boolean(activeEntityFormErrors.startupDate)}
                              helperText={activeEntityFormErrors.startupDate}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Select an entity to complete the required customer information.
                    </Typography>
                  )}

                </React.Fragment>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {step === 1 ? (
            <Button onClick={() => setStep(0)} disabled={converting}>
              Back
            </Button>
          ) : null}
          <Button onClick={handleClose} disabled={converting}>
            Cancel
          </Button>
          {step === 0 ? (
            <Button
              onClick={handleContinueToDetails}
              variant="contained"
              color="primary"
              disabled={loading || entities.length === 0 || selectedEntityIds.length === 0}
            >
              Continue
            </Button>
          ) : (
            <Button
              onClick={handleConvert}
              variant="contained"
              color="primary"
              disabled={converting || selectedEntityIds.length === 0}
              startIcon={converting ? <CircularProgress size={20} /> : null}
            >
              {converting
                ? "Converting..."
                : selectedEntityIds.length > 1
                  ? "Convert Entities to Customer"
                  : "Convert Entity to Customer"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={resultDialogOpen}
        onClose={() => {
          setResultDialogOpen(false);
          setResult(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{result?.success ? "Conversion Successful" : "Conversion Failed"}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {result?.message}
          </Typography>

          {result?.success && result?.createdCustomers?.length > 0 ? (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                Created Customers
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List dense disablePadding>
                {result.createdCustomers.map((customer, index) => (
                  <ListItemButton key={`${customer.entityNumber || "customer"}-${index}`} disableRipple>
                    <ListItemText
                      primary={`${customer.entityNumber || "N/A"} - ${customer.name || "Unnamed Customer"}`}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setResultDialogOpen(false);
              setResult(null);
            }}
            variant="contained"
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default ConvertProspectEntitiesDialog;
